import React, { useState } from 'react';
import { BanknoteIcon, ActivityIcon, TicketIcon, AlertTriangleIcon, SearchIcon, FileTextIcon, CalendarIcon } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { StatCard } from '../components/StatCard';
import { useMavisData, formatZAR } from '../hooks/useMavisData';
import { CheckMeterModal } from '../components/CheckMeterModal';
export function Dashboard() {
  const data = useMavisData();
  const [isCheckMeterOpen, setIsCheckMeterOpen] = useState(false);
  const [dateRange, setDateRange] = useState('today');
  const today = new Date().toLocaleDateString('en-ZA', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  return <div className="p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500" data-id="element-921">
      <div className="flex justify-between items-end" data-id="element-922">
        <div data-id="element-923">
          <h1 className="text-3xl font-bold text-gray-900 mb-1" data-id="element-924">
            Dashboard Overview
          </h1>
          <p className="text-gray-500" data-id="element-925">{today}</p>
        </div>
        <div className="flex gap-3" data-id="element-926">
          <div className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-none shadow-sm" data-id="element-927">
            <CalendarIcon className="w-4 h-4 text-mavis-teal" data-id="element-928" />
            <select value={dateRange} onChange={e => setDateRange(e.target.value)} className="bg-transparent border-0 text-sm font-medium text-gray-900 focus:ring-0 focus:outline-none cursor-pointer pr-6" data-id="element-929">

              <option value="today" data-id="element-930">Today</option>
              <option value="yesterday" data-id="element-931">Yesterday</option>
              <option value="7days" data-id="element-932">Last 7 Days</option>
              <option value="30days" data-id="element-933">Last 30 Days</option>
              <option value="this-month" data-id="element-934">This Month</option>
              <option value="last-month" data-id="element-935">Last Month</option>
              <option value="this-quarter" data-id="element-936">This Quarter</option>
            </select>
          </div>
          <button onClick={() => setIsCheckMeterOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-none hover:bg-gray-50 transition-colors shadow-sm font-medium" data-id="element-937">

            <SearchIcon className="w-4 h-4" data-id="element-938" />
            Check Meter
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-939">
        <StatCard title="Total Revenue (Today)" value={formatZAR(data.dashboard.revenue)} change={data.dashboard.revenueChange} icon={<BanknoteIcon className="w-6 h-6" data-id="element-941" />} accentColor="teal" data-id="element-940" />

        <StatCard title="Active Smart Meters" value={data.dashboard.activeMeters.toLocaleString()} change={data.dashboard.metersChange} icon={<ActivityIcon className="w-6 h-6" data-id="element-943" />} accentColor="green" data-id="element-942" />

        <StatCard title="Tokens Sold Today" value={data.dashboard.tokensSold.toLocaleString()} change={data.dashboard.tokensChange} icon={<TicketIcon className="w-6 h-6" data-id="element-945" />} accentColor="gold" data-id="element-944" />

        <StatCard title="Pending Alerts" value={data.dashboard.pendingAlerts} change={data.dashboard.alertsChange} icon={<AlertTriangleIcon className="w-6 h-6" data-id="element-947" />} accentColor="orange" data-id="element-946" />

      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-948">
        <div className="lg:col-span-2 bg-white p-6 rounded-none shadow-sm border border-gray-100" data-id="element-949">
          <div className="flex justify-between items-center mb-6" data-id="element-950">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-951">
              Revenue Trend (7 Days)
            </h2>
            <button className="text-sm text-mavis-teal hover:underline flex items-center gap-1" data-id="element-952">
              <FileTextIcon className="w-4 h-4" data-id="element-953" /> View Report
            </button>
          </div>
          <div className="h-72 w-full" data-id="element-954">
            <ResponsiveContainer width="100%" height="100%" data-id="element-955">
              <AreaChart data={data.revenueChart} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0
            }} data-id="element-956">

                <defs data-id="element-957">
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1" data-id="element-958">
                    <stop offset="5%" stopColor="#0D4F5C" stopOpacity={0.3} data-id="element-959" />
                    <stop offset="95%" stopColor="#0D4F5C" stopOpacity={0} data-id="element-960" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" data-id="element-961" />

                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} dy={10} data-id="element-962" />

                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} tickFormatter={value => `R${value / 1000}k`} dx={-10} data-id="element-963" />

                <Tooltip formatter={(value: number) => [formatZAR(value), 'Revenue']} contentStyle={{
                borderRadius: '0',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-964" />

                <Area type="monotone" dataKey="revenue" stroke="#0D4F5C" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" data-id="element-965" />

              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-none shadow-sm border border-gray-100 flex flex-col" data-id="element-966">
          <div className="flex justify-between items-center mb-6" data-id="element-967">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-968">
              Recent Transactions
            </h2>
          </div>
          <div className="flex-1 overflow-auto" data-id="element-969">
            <div className="space-y-4" data-id="element-970">
              {data.recentTransactions.map(txn => <div key={txn.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-none transition-colors border border-transparent hover:border-gray-100" data-id="element-971">

                  <div data-id="element-972">
                    <p className="font-mono text-sm font-medium text-gray-900" data-id="element-973">
                      {txn.meter}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-974">
                      {txn.time} • {txn.id}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-975">
                    <p className="font-bold text-gray-900 font-mono" data-id="element-976">
                      {formatZAR(txn.amount)}
                    </p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-none font-medium ${txn.status === 'Success' ? 'bg-mavis-green/10 text-mavis-green' : 'bg-mavis-orange/10 text-mavis-orange'}`} data-id="element-977">

                      {txn.status}
                    </span>
                  </div>
                </div>)}
            </div>
          </div>
          <button className="w-full mt-4 py-2 text-sm text-mavis-teal font-medium hover:bg-mavis-teal/5 rounded-none transition-colors" data-id="element-978">
            View All Transactions
          </button>
        </div>
      </div>

      <CheckMeterModal isOpen={isCheckMeterOpen} onClose={() => setIsCheckMeterOpen(false)} data-id="element-979" />

    </div>;
}